/**
 * React Query mutation for swiping on users
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { discoveryQueryKeys } from "../queries/useDiscoveryUsers";
import { likedYouQueryKeys } from "../queries/useLikedYouUsers";
import { discoveryService } from "../services/discoveryService";
import type { Match } from "../types";

/**
 * Data structure for swipe user mutation
 */
export interface SwipeUserData {
  /** ID of the user performing the swipe */
  swiperId: string;
  /** ID of the user being swiped on */
  swipedUserId: string;
  /** Direction of the swipe */
  direction: "left" | "right";
}

/**
 * Options for the swipe user mutation
 */
export interface UseSwipeUserOptions {
  /** Callback when swipe is successful */
  onSuccess?: (match: Match | null, variables: SwipeUserData, context: unknown) => void;
  /** Callback when swipe fails */
  onError?: (error: Error, variables: SwipeUserData, context: unknown) => void;
  /** Callback when swipe completes (success or error) */
  onSettled?: (
    match: Match | null | undefined,
    error: Error | null,
    variables: SwipeUserData,
    context: unknown
  ) => void;
}

/**
 * Hook to record a user swipe action
 */
export function useSwipeUser(options?: UseSwipeUserOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SwipeUserData) => {
      return await discoveryService.recordSwipe(data.swiperId, data.swipedUserId, data.direction);
    },
    onSuccess: (match, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: discoveryQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: likedYouQueryKeys.list(variables.swiperId),
      });

      // If a match occurred, also invalidate matches queries
      if (match) {
        queryClient.invalidateQueries({
          queryKey: ["matches"],
        });
        queryClient.invalidateQueries({
          queryKey: ["chats"],
        });
      }

      options?.onSuccess?.(match, variables, context);
    },
    ...(options?.onError && { onError: options.onError }),
    ...(options?.onSettled && { onSettled: options.onSettled }),
  });
}
